import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { MicNone as MicIcon } from '@mui/icons-material';
import { lionVoiceAPI } from '../services/api';
import { colors } from '../theme';

const LionVoicePage = () => {
  const { data, isLoading, error } = useQuery(
    'lionvoice-episodes',
    () => lionVoiceAPI.getAll({ ordering: 'episode_number' })
  );

  const episodes = data?.data?.results || data?.data || [];
  const itemsPerPage = 10;

  const tabRanges = useMemo(() => {
    if (!episodes.length) return [];

    const sortedEpisodes = [...episodes].sort((a, b) => a.episode_number - b.episode_number);
    const ranges = [];
    const totalPages = Math.ceil(sortedEpisodes.length / itemsPerPage);

    for (let i = 0; i < totalPages; i++) {
      const startIndex = i * itemsPerPage;
      const endIndex = Math.min(startIndex + itemsPerPage - 1, sortedEpisodes.length - 1);
      const startEpNum = sortedEpisodes[startIndex]?.episode_number || startIndex + 1;
      const endEpNum = sortedEpisodes[endIndex]?.episode_number || endIndex + 1;
      ranges.push({ label: `${startEpNum}-${endEpNum}`, startIndex, endIndex });
    }
    return ranges;
  }, [episodes]);

  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const currentEpisodes = useMemo(() => {
    if (!episodes.length || tabRanges.length === 0) return [];
    const range = tabRanges[currentTab] || tabRanges[0];
    const sortedEpisodes = [...episodes].sort((a, b) => a.episode_number - b.episode_number);
    return sortedEpisodes.slice(range.startIndex, range.endIndex + 1);
  }, [episodes, currentTab, tabRanges]);

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colors.royalBlue} 0%, #1565C0 100%)`,
          color: '#fff',
          py: { xs: 4, md: 6 },
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <MicIcon sx={{ fontSize: 40, color: colors.lionsGold }} />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
              獅聲心語
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ maxWidth: 720, lineHeight: 1.8, opacity: 0.92 }}>
            由國際獅子會中國港澳303區呈獻《獅聲心語》，主持黎莉請嚟多位總監及屬會代表，分享真摯故事，讓大眾更深入了解獅子會嘅使命。新城知訊台逢星期三早上十至十一播出，亦可透過新城網站及手機App重溫。
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.8, fontStyle: 'italic' }}>
              港澳303區各位獅兄獅姐，快啲聯絡屬會代表，分享你嘅服務經歷，一齊用聲音傳遞獅子精神啦！
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            載入節目資料時發生錯誤，請稍後再試。
          </Alert>
        )}

        {!isLoading && episodes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <MicIcon sx={{ fontSize: 64, color: colors.gray, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              暫時未有節目資料
            </Typography>
          </Box>
        )}

        {!isLoading && tabRanges.length > 0 && (
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              textColor="primary"
              indicatorColor="primary"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: colors.lionsGold, height: 3 },
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  minWidth: 100,
                  '&.Mui-selected': { color: colors.royalBlue },
                },
              }}
            >
              {tabRanges.map((range, index) => (
                <Tab key={index} label={range.label} />
              ))}
            </Tabs>
          </Box>
        )}

        <Grid container spacing={3}>
          {currentEpisodes.map((episode) => (
            <Grid item xs={12} sm={6} md={4} key={episode.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <CardActionArea
                  component={RouterLink}
                  to={`/episodes/${episode.id}`}
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {episode.banner_image ? (
                      <CardMedia
                        component="img"
                        height="180"
                        image={episode.banner_image}
                        alt={episode.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          background: `linear-gradient(135deg, ${colors.royalBlue} 0%, #1565C0 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <MicIcon sx={{ fontSize: 56, color: 'rgba(255,255,255,0.4)' }} />
                      </Box>
                    )}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        backgroundColor: colors.lionsGold,
                        color: colors.royalBlue,
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 1,
                        lineHeight: 1.4,
                      }}
                    >
                      第{episode.episode_number}集
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      sx={{
                        fontWeight: 700,
                        fontSize: '1rem',
                        mb: 1,
                        color: colors.royalBlue,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {episode.title}
                    </Typography>

                    {episode.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.6,
                        }}
                      >
                        {episode.description}
                      </Typography>
                    )}

                    {episode.published_date && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 1 }}>
                        {new Date(episode.published_date).toLocaleDateString('zh-HK', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </Typography>
                    )}

                    {episode.guests && episode.guests.length > 0 && (
                      <Box sx={{ mt: 1.5 }}>
                        {episode.guests.map((guest, idx) => (
                          <Typography
                            key={idx}
                            variant="caption"
                            sx={{ display: 'block', lineHeight: 1.6, color: colors.textSecondary }}
                          >
                            {guest.name}{guest.role ? ` | ${guest.role}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default LionVoicePage;
